/**
 * S2 · Add a stat, step by step.
 *
 * Eight short questions in plain words, and a preview on the right that runs
 * the real thing against real data while you fill the form. Save stays
 * disabled until that preview runs clean and gives the same rows twice —
 * a stat that answers a question two ways is not a stat.
 *
 * Nobody has to know the sport's vocabulary to use it. Every box that takes a
 * fact key offers the sport's facts as you type, with what each one means; the
 * sort box offers only the numbers this stat produces; the filter offers the
 * facts and the stamps every line carries. The first fact of the sport is the
 * starting point, the key is worked out from the name, and the rail says in
 * one sentence what the stat you are building will do.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  Button,
  Card,
  Combobox,
  Empty,
  Field,
  FieldRow,
  Input,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Row,
  Rows,
  Section,
  Select,
  Split,
  Stack,
  Steps,
  Table,
  Td,
  Th,
  Toolbar,
  type Suggestion,
} from "@/components/kit";
import { backToStats } from "@/lib/statsEngine/api";
import {
  loadVocabulary,
  previewStat,
  saveStat,
  type PreviewResult,
  type Vocabulary,
} from "@/lib/statsEngine/authoring";
import { useApi } from "@/spa/useApi";

const STEPS = ["Kind", "Reads", "Where", "Filter", "Shape", "When", "Edits", "Name"];

/** What each step is really asking, in the words a person would use. */
const PROMPTS = [
  "What shape of answer do you want?",
  "Which numbers off a match line does it use?",
  "Which part of a match counts, and is it split up?",
  "Should it skip some lines?",
  "How is the answer ordered or worked out?",
  "How soon do the numbers have to move?",
  "May anyone override a number by hand?",
  "What is it called, and what does it mean?",
];

const KINDS = [
  { value: "total", label: "A total (added up per player or team)" },
  { value: "rank", label: "A ranking (a sorted list)" },
  { value: "rate", label: "A rate (one number divided by another)" },
  { value: "count_where", label: "A count of matching lines" },
  { value: "best", label: "The best single line" },
  { value: "medal_table", label: "A medal table (gold, silver, bronze)" },
];

const AGGREGATES = [
  { value: "sum", label: "added up" },
  { value: "count", label: "how many lines" },
  { value: "min", label: "the lowest" },
  { value: "max", label: "the highest" },
];

const OPS = [
  { value: "gte", label: "is at least" },
  { value: "gt", label: "is more than" },
  { value: "eq", label: "is exactly" },
  { value: "ne", label: "is not" },
  { value: "lt", label: "is less than" },
  { value: "lte", label: "is at most" },
];

const SPLITS = [
  { value: "", label: "no split — one row per player or team" },
  { value: "team", label: "by the side they played for" },
  { value: "opponent", label: "by who they played against" },
  { value: "venue", label: "by where the match was played" },
  { value: "format", label: "by the format it was played to" },
];

/** Kinds that carry as many numbers as you like. The generic ``total``,
 *  ``rank`` and ``table`` functions loop over every column; the rest read
 *  ``columns[0]`` and ignore the others, so only these may add a second. */
const MULTI = new Set(["total", "rank"]);

/** Kinds that name exactly one output but still read a fact off the line. */
const SINGLE = new Set(["best", "count_where"]);

type Column = { name: string; reads: string; aggregate: string };

/** `Most sixes` → `most_sixes`. The key is where the rows live, so it is made
 *  of nothing a URL or a partner's code would trip over. */
function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function AddStatPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const [params] = useSearchParams();
  // Where the preview runs. The URL wins when the person came from a level's
  // Stats tab; otherwise they pick, because "Add a stat" from the sport's own
  // tab carries no scope and a preview needs one — without this the form's
  // main entry point led to a preview that could never run and a Save that
  // could never unlock.
  const urlRef = params.get("ref") ?? "";
  const urlKind = params.get("kind") ?? "";
  const [chosen, setChosen] = useState("");

  const loadWords = useCallback(() => loadVocabulary(sport), [sport]);
  const { data: vocab, error: vocabError } = useApi<Vocabulary>(
    loadWords,
    [sport],
    "the admin API is not reachable",
  );

  const [step, setStep] = useState(0);
  const [fn, setFn] = useState("rank");
  const [columns, setColumns] = useState<Column[]>([{ name: "", reads: "", aggregate: "sum" }]);
  const [orderBy, setOrderBy] = useState("");
  const [limit, setLimit] = useState("10");
  const [segment, setSegment] = useState("any");
  const [split, setSplit] = useState("");
  const [filterField, setFilterField] = useState("");
  const [filterOp, setFilterOp] = useState("gte");
  const [filterValue, setFilterValue] = useState("");
  const [numerator, setNumerator] = useState("");
  const [denominator, setDenominator] = useState("");
  const [factor, setFactor] = useState("100");
  const [tier, setTier] = useState("settled");
  const [edit, setEdit] = useState("cell");
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  // Once a person has typed a key by hand it is theirs; until then it follows
  // the name, so nobody has to invent one.
  const keyTouched = useRef(false);

  const scopes = useMemo(() => vocab?.scopes ?? [], [vocab]);
  const picked = useMemo(
    () =>
      scopes.find((s) => `${s.kind}:${s.ref}` === chosen) ??
      (urlRef
        ? { kind: urlKind || "node", ref: urlRef, label: "where you came from", fixtures: 0 }
        : scopes[0]),
    [scopes, chosen, urlKind, urlRef],
  );
  const scopeKind = picked?.kind ?? "node";
  const scopeRef = picked?.ref ?? "";

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState("");

  // The sport's first fact is the starting point — a form that opens on
  // cricket's `sixes` for a swimming meet is a form that opens wrong.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !vocab || vocab.facts.length === 0) return;
    started.current = true;
    const first = vocab.facts[0].value;
    const second = vocab.facts[1]?.value ?? first;
    setColumns([{ name: first, reads: first, aggregate: "sum" }]);
    setOrderBy(first);
    setNumerator(first);
    setDenominator(second);
    setName(`Most ${first.replace(/_/g, " ")}`);
    setDescription(`${vocab.facts[0].note} — added up over this competition.`);
  }, [vocab]);

  useEffect(() => {
    if (!keyTouched.current) setKey(name ? `${sport}.${slug(name)}` : "");
  }, [name, sport]);

  const factWords: Suggestion[] = useMemo(
    () => (vocab?.facts ?? []).map((w) => ({ value: w.value, note: w.note, kind: "fact" })),
    [vocab],
  );
  const filterWords: Suggestion[] = useMemo(
    () => [
      ...factWords,
      ...(vocab?.columns ?? []).map((w) => ({ value: w.value, note: w.note, kind: "line" })),
    ],
    [factWords, vocab],
  );
  const outputs = columns.map((c) => c.name).filter(Boolean);
  const seenParts = (vocab?.segments ?? []).map((s) => s.value);

  const spec = useMemo(() => {
    const built: Record<string, unknown> = {
      function: fn,
      reads: columns.map((c) => c.reads).filter(Boolean),
      columns: columns.filter((c) => c.name && c.reads),
      segment,
      split: split || null,
      filters:
        filterField && filterValue !== ""
          ? [{ field: filterField, op: filterOp, value: Number(filterValue) }]
          : [],
    };
    if (fn === "rank" || fn === "best") {
      built.order_by = orderBy || outputs[0] || "";
      built.descending = true;
    }
    if (fn === "rank" && limit) built.limit = Number(limit);
    if (fn === "rate") {
      built.numerator = numerator;
      built.denominator = denominator;
      built.factor = Number(factor) || 1;
      built.reads = [numerator, denominator].filter(Boolean);
    }
    return built;
    // `outputs` is derived from `columns`, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn, columns, segment, split, filterField, filterOp, filterValue, orderBy, limit, numerator, denominator, factor]);

  const runPreview = useCallback(async () => {
    setBusy(true);
    setSaid("");
    try {
      setPreview(await previewStat(spec, scopeKind, scopeRef));
    } catch (cause) {
      setSaid(cause instanceof Error ? cause.message : "the preview could not run");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }, [scopeKind, scopeRef, spec]);

  const save = useCallback(async () => {
    setBusy(true);
    setSaid("");
    try {
      const saved = await saveStat({
        key,
        name,
        sportCode: sport,
        scopeKind,
        spec: { ...spec, preview_scope_ref: scopeRef },
        description,
        tier,
        edit,
      });
      setSaid(`Saved ${saved.key} version ${saved.version} (${saved.status}).`);
    } catch (cause) {
      setSaid(cause instanceof Error ? cause.message : "could not save");
    } finally {
      setBusy(false);
    }
  }, [description, edit, key, name, scopeKind, scopeRef, spec, sport, tier]);

  const previewColumns =
    preview && preview.rows.length > 0
      ? Object.keys(preview.rows[0]).filter((c) => c !== "split_key")
      : [];

  const setColumn = (i: number, patch: Partial<Column>) =>
    setColumns((all) => all.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  /** A new fact picked: the output takes its name unless somebody renamed it. */
  const readsChanged = (i: number, reads: string) => {
    const was = columns[i];
    const followed = was.name === "" || was.name === was.reads;
    setColumn(i, { reads, ...(followed ? { name: reads } : {}) });
    if (i === 0 && (orderBy === "" || orderBy === was.name)) setOrderBy(followed ? reads : was.name);
  };

  const sentence = describe({
    fn,
    columns,
    orderBy: orderBy || outputs[0] || "",
    limit,
    segment,
    split,
    filterField,
    filterOp,
    filterValue,
    numerator,
    denominator,
    factor,
    scopeKind,
  });

  if (vocabError) return <Problem>Could not reach the admin service: {vocabError}</Problem>;

  return (
    <Stack>
      <PageHead
        title="Add a stat"
        note="Eight short questions. Every box offers the sport's own words as you type, and the preview runs the real thing against real data before you save."
        actions={
          <Toolbar>
            <LinkButton to={backToStats(sport, urlKind, urlRef)}>Back to Stats</LinkButton>
            <Button
              tone="primary"
              disabled={busy || !preview?.ok || !key || !name}
              title={preview?.ok ? "" : "the preview has to run clean first"}
              onClick={save}
            >
              Save
            </Button>
          </Toolbar>
        }
      />

      <Steps steps={STEPS} at={step} onGo={setStep} />

      <Split
        rail={
          <>
            <Card>
              <Section title="In plain words" note="What the stat you are building will do.">
                <p className="muted">{sentence}</p>
              </Section>
            </Card>
            {scopes.length > 0 ? (
              <Card>
                <Field
                  label="Preview against"
                  hint={`${picked?.fixtures ?? 0} fixture(s). The stat itself counts wherever you attach it — this only decides what the preview reads.`}
                >
                  <Select
                    value={chosen || (picked ? `${picked.kind}:${picked.ref}` : "")}
                    onChange={(e) => setChosen(e.target.value)}
                  >
                    {urlRef && !scopes.some((s) => s.ref === urlRef) ? (
                      <option value={`${urlKind}:${urlRef}`}>where you came from</option>
                    ) : null}
                    {scopes.map((s) => (
                      <option key={`${s.kind}:${s.ref}`} value={`${s.kind}:${s.ref}`}>
                        {s.label} ({s.fixtures})
                      </option>
                    ))}
                  </Select>
                </Field>
              </Card>
            ) : null}
            <Card>
              <PreviewRail
                preview={preview}
                busy={busy}
                columns={previewColumns}
                onRun={runPreview}
              />
            </Card>
            {vocab && vocab.stats.length > 0 ? (
              <Card>
                <Section
                  title="Already here"
                  note="Stats this sport already has. Worth a look before adding one that says the same thing."
                >
                  <ul className="muted" style={{ margin: 0, paddingLeft: "1.1em" }}>
                    {vocab.stats.slice(0, 8).map((s) => (
                      <li key={s.value}>
                        <span className="mono">{s.value}</span> — {s.note}
                      </li>
                    ))}
                  </ul>
                </Section>
              </Card>
            ) : null}
          </>
        }
      >
        <Card>
          <Section title={`${step + 1}. ${STEPS[step]}`} note={PROMPTS[step]}>
            {step === 0 ? (
              <Field label="What kind of stat is this?">
                <Select value={fn} onChange={(e) => setFn(e.target.value)}>
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}

            {step === 1 ? (
              MULTI.has(fn) ? (
                /* Numbered rows with their own remove control, because the
                   order is the order a partner reads the columns in — and
                   because a button wrapped in a blank-labelled field lands on
                   a line of its own the moment the grid runs out of room. */
                <Rows
                  count={columns.length}
                  empty="No numbers yet."
                  addLabel="Add another number"
                  onAdd={() =>
                    setColumns((c) => [...c, { name: "", reads: "", aggregate: "sum" }])
                  }
                  onMove={(from, to) =>
                    setColumns((c) => {
                      const next = [...c];
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved);
                      return next;
                    })
                  }
                  onRemove={(i) =>
                    setColumns((c) => (c.length > 1 ? c.filter((_, j) => j !== i) : c))
                  }
                >
                  {columns.map((column, i) => (
                    <Row
                      key={i}
                      index={i}
                      badge={column.name ? <Pill>{column.name}</Pill> : undefined}
                    >
                      <Field
                        label="Reads this number"
                        hint={
                          factWords.length
                            ? "Start typing; the sport's facts appear with what each means."
                            : "This sport declares no facts yet."
                        }
                      >
                        <Combobox
                          value={column.reads}
                          onChange={(v) => readsChanged(i, v)}
                          suggestions={factWords}
                          placeholder={factWords[0]?.value ?? "a fact"}
                          free={false}
                        />
                      </Field>
                      <Field label="How">
                        <Select
                          value={column.aggregate}
                          onChange={(e) => setColumn(i, { aggregate: e.target.value })}
                        >
                          {AGGREGATES.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Call the answer" hint="The column a partner reads.">
                        <Input
                          value={column.name}
                          onChange={(e) => setColumn(i, { name: slug(e.target.value) })}
                        />
                      </Field>
                    </Row>
                  ))}
                </Rows>
              ) : SINGLE.has(fn) ? (
                <Stack>
                  <FieldRow>
                    <Field
                      label={fn === "count_where" ? "Counts lines carrying" : "Reads this number"}
                      hint="Start typing; the sport's facts appear with what each means."
                    >
                      <Combobox
                        value={columns[0].reads}
                        onChange={(v) => readsChanged(0, v)}
                        suggestions={factWords}
                        placeholder={factWords[0]?.value ?? "a fact"}
                        free={false}
                      />
                    </Field>
                    <Field label="Call the answer" hint="The column a partner reads.">
                      <Input
                        value={columns[0].name}
                        onChange={(e) => setColumn(0, { name: slug(e.target.value) })}
                      />
                    </Field>
                  </FieldRow>
                  <p className="muted">
                    {fn === "best"
                      ? "The best single line is one number by definition, so there is one to name."
                      : "A count is one number: how many lines matched. Set what counts in step 4."}
                  </p>
                </Stack>
              ) : (
                <Empty
                  title="Nothing to choose here"
                  note={
                    fn === "medal_table"
                      ? "A medal table reads where people finished, which is already on every line. Go on to the next step."
                      : "A rate divides one number by another, and both are picked in step 5. Go on."
                  }
                />
              )
            ) : null}

            {step === 2 ? (
              <Stack>
                <Field
                  label="Which part of a match counts?"
                  hint={
                    seenParts.length
                      ? `This sport's lines are split into: ${seenParts.join(", ")}.`
                      : "This sport writes one line per match, so the whole match is the only choice that changes anything."
                  }
                >
                  <Select value={segment} onChange={(e) => setSegment(e.target.value)}>
                    <option value="any">every line — the parts and the whole</option>
                    <option value="whole">the whole match only</option>
                    <option value="innings">each part separately (an innings, a half, a leg)</option>
                  </Select>
                </Field>
                <Field label="Split by" hint="Optional. One row per value instead of one in total.">
                  <Select value={split} onChange={(e) => setSplit(e.target.value)}>
                    {SPLITS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </Stack>
            ) : null}

            {step === 3 ? (
              <Stack>
                <FieldRow>
                  <Field
                    label="Only lines where"
                    hint="A fact off the line, or one of the stamps every line carries. Leave empty to count every line."
                  >
                    <Combobox
                      value={filterField}
                      onChange={setFilterField}
                      suggestions={filterWords}
                      placeholder="nothing — count every line"
                    />
                  </Field>
                  <Field label="…">
                    <Select value={filterOp} onChange={(e) => setFilterOp(e.target.value)}>
                      {OPS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="this number">
                    <Input
                      inputMode="decimal"
                      placeholder="20"
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                    />
                  </Field>
                </FieldRow>
                {filterField ? (
                  <Toolbar>
                    <Button
                      onClick={() => {
                        setFilterField("");
                        setFilterValue("");
                      }}
                    >
                      Clear the filter
                    </Button>
                  </Toolbar>
                ) : null}
              </Stack>
            ) : null}

            {step === 4 ? (
              fn === "rate" ? (
                <FieldRow>
                  <Field label="Divide this" hint="A fact off the line.">
                    <Combobox
                      value={numerator}
                      onChange={setNumerator}
                      suggestions={factWords}
                      free={false}
                    />
                  </Field>
                  <Field label="by this">
                    <Combobox
                      value={denominator}
                      onChange={setDenominator}
                      suggestions={factWords}
                      free={false}
                    />
                  </Field>
                  <Field label="times" hint="100 turns a fraction into a percentage.">
                    <Input inputMode="decimal" value={factor} onChange={(e) => setFactor(e.target.value)} />
                  </Field>
                </FieldRow>
              ) : fn === "rank" || fn === "best" ? (
                <FieldRow>
                  <Field label="Sort by" hint="One of the numbers this stat produces (step 2).">
                    <Select value={orderBy || outputs[0] || ""} onChange={(e) => setOrderBy(e.target.value)}>
                      {outputs.length === 0 ? <option value="">— pick a number in step 2 first —</option> : null}
                      {outputs.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {fn === "rank" ? (
                    <Field label="How many rows">
                      <Input inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value)} />
                    </Field>
                  ) : null}
                </FieldRow>
              ) : (
                <Empty
                  title="Nothing to shape"
                  note={
                    fn === "total"
                      ? "A total is one row per player or team with each number added up. There is no order to choose."
                      : fn === "count_where"
                        ? "A count is one number per player or team: how many lines matched the filter in step 4."
                        : "A medal table is always gold, silver, bronze, in that order."
                  }
                />
              )
            ) : null}

            {step === 5 ? (
              <Field label="When should it update?">
                <Select value={tier} onChange={(e) => setTier(e.target.value)}>
                  <option value="live">as the match is scored (live)</option>
                  <option value="settled">after the match ends</option>
                  <option value="periodic">every night</option>
                </Select>
              </Field>
            ) : null}

            {step === 6 ? (
              <Field label="Who may change the numbers by hand?">
                <Select value={edit} onChange={(e) => setEdit(e.target.value)}>
                  <option value="cell">ops may edit one number, with a reason</option>
                  <option value="none">nobody</option>
                </Select>
              </Field>
            ) : null}

            {step === 7 ? (
              <Stack>
                <Field label="What is it called?">
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field
                  label="Its permanent key"
                  hint="Worked out from the name until you change it. This never changes once saved — it is where the rows live."
                >
                  <Input
                    value={key}
                    className="mono"
                    onChange={(e) => {
                      keyTouched.current = true;
                      setKey(e.target.value);
                    }}
                  />
                </Field>
                <Field label="What does it mean?" hint="This is what a partner reads.">
                  <Input value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>
              </Stack>
            ) : null}
          </Section>
        </Card>

        <Toolbar>
          <Button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            ← Back
          </Button>
          <Button
            disabled={step === STEPS.length - 1}
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
          >
            Next →
          </Button>
          <Button tone="primary" disabled={busy} onClick={runPreview}>
            {busy ? "Running…" : "Run preview"}
          </Button>
        </Toolbar>

        {said ? <Card>{said}</Card> : null}
      </Split>
    </Stack>
  );
}

/** The stat, as one sentence a person would say. Kept beside the form so a
 *  wrong answer in step 2 reads wrong before step 8. */
function describe(s: {
  fn: string;
  columns: Column[];
  orderBy: string;
  limit: string;
  segment: string;
  split: string;
  filterField: string;
  filterOp: string;
  filterValue: string;
  numerator: string;
  denominator: string;
  factor: string;
  scopeKind: string;
}): string {
  const words = (v: string) => v.replace(/_/g, " ");
  const aggWord: Record<string, string> = { sum: "the total of", count: "how many lines carry", min: "the lowest", max: "the highest" };
  const over = s.scopeKind === "fixture" ? "in this match" : "over every match here";
  const part =
    s.segment === "whole" ? ", whole-match lines only" : s.segment === "innings" ? ", each part of a match separately" : "";
  const filter =
    s.filterField && s.filterValue !== ""
      ? `, only where ${words(s.filterField)} ${OPS.find((o) => o.value === s.filterOp)?.label ?? s.filterOp} ${s.filterValue}`
      : "";
  const split = s.split ? `, one row per ${SPLITS.find((x) => x.value === s.split)?.label.replace(/^by /, "") ?? s.split}` : "";
  const cols = s.columns.filter((c) => c.reads);
  const list = cols.map((c) => `${aggWord[c.aggregate] ?? c.aggregate} ${words(c.reads)}`).join(" and ");
  switch (s.fn) {
    case "total":
      return `For each player or team, ${list || "…"} ${over}${part}${filter}${split}.`;
    case "rank":
      return `Rank players or teams by ${words(s.orderBy) || "…"} (${list || "…"}) ${over}${part}${filter}${split}; keep the top ${s.limit || "…"}.`;
    case "best":
      return `Each player or team's single best ${words(s.orderBy) || "…"} ${over}${part}${filter}${split}.`;
    case "rate":
      return `${words(s.numerator) || "…"} divided by ${words(s.denominator) || "…"}${Number(s.factor) !== 1 ? `, times ${s.factor}` : ""}, per player or team ${over}${part}${filter}${split}.`;
    case "count_where":
      return `How many lines each player or team has ${over}${part}${filter || " — add a filter in step 4 or this counts every line"}${split}.`;
    case "medal_table":
      return `Gold, silver and bronze per player or team, from where they finished, ${over}${filter}.`;
    default:
      return "";
  }
}

/**
 * The preview: what the engine really returns, while the form is being filled
 * in. The marks below it are the checks a saved stat has to pass, shown as
 * they stand rather than only when they fail.
 */
function PreviewRail({
  preview,
  busy,
  columns,
  onRun,
}: {
  preview: PreviewResult | null;
  busy: boolean;
  columns: string[];
  onRun: () => void;
}) {
  if (!preview) {
    return (
      <Empty
        title="No preview yet"
        note="Run it to see the rows this stat really returns, out of the real database."
        // Not primary: the same action already sits under the form as the
        // step you are meant to take. Two blue buttons on one screen is two
        // places to look for one thing.
        action={
          <Button disabled={busy} onClick={onRun}>
            {busy ? "Running…" : "Run preview"}
          </Button>
        }
      />
    );
  }
  if (preview.error) return <Problem>{preview.error}</Problem>;
  return (
    <Section
      title="Preview"
      note={`Ran in ${preview.milliseconds} ms over ${preview.matches} matches.`}
    >
      <Stack>
        <Table>
          <thead>
            <tr>
              {columns.map((c) => (
                <Th key={c}>{c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.rows.slice(0, 10).map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <Td key={c}>{String(row[c])}</Td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        <Toolbar>
          <Pill tone={preview.sameTwice ? "good" : "bad"} dot>
            {preview.sameTwice ? "Ran twice, same rows" : "Two runs, two answers"}
          </Pill>
          {preview.marks.map((m) => (
            <Pill key={m.rule} tone={m.ok ? (m.warn ? "warn" : "good") : "bad"} dot>
              <span title={m.note}>{m.rule}</span>
            </Pill>
          ))}
        </Toolbar>

        <details>
          <summary>Show the SQL it runs</summary>
          <pre className="mono">{preview.sql}</pre>
        </details>
      </Stack>
    </Section>
  );
}
